<script setup lang="ts">
import DJIcon from '~/components/ui/icon/Icon.vue'
import { IconStarFill } from '~/components/ui/icon/icons'
import { useTheme } from '~/composables/common/useTheme'

interface Props {
  rate: number | string
  max?: number
  color?: {
    active: string
    inactive: string
  }
  size?: number
  readOnly?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  rate: 4,
  max: 5,
  color: () => ({
    active: '#D0A61A', // brand Gold Accent (§7) — rate stars are a legal gold use; was #F79009
    inactive: '#DFDFDF',
  }),
  size: 16,
  readOnly: true,
})
const emits = defineEmits(['update:rate', 'selectRate'])

// v2 dark mode (Wave 7/V30c) — the default inactive star (#DFDFDF) glares on the dark canvas;
// drop it to a subtle dark grey in dark. Active gold #D0A61A reads fine on dark (§1001).
// JS-prop-driven colour (bound to DJIcon :color), so it can't be flipped by the CSS seam.
const { isDark } = useTheme()
const inactiveColor = computed(() => (isDark.value ? '#3A3A3A' : props.color.inactive))

const numberRate = computed(() => (typeof props.rate === 'string' ? parseFloat(props.rate) : props.rate))
const valueDecimal = computed(() => numberRate.value * 100 - Math.floor(numberRate.value) * 100)
const decimalStyle = computed(() => {
  return {
    color: props.color.active,
    width: `${valueDecimal.value}%`,
  }
})
const currentValue = ref(numberRate.value)
const selected = ref(false)
const hoverIndex = ref(-1)

function showDecimalIcon(item: number) {
  const showWhenDisabled = valueDecimal.value > 0 && item - 1 < currentValue.value && item > currentValue.value
  const showWhenAllowHalf = item - 0.5 <= currentValue.value && item > currentValue.value
  return showWhenDisabled || showWhenAllowHalf
}

function setCurrentValue(value: number) {
  if (props.readOnly) {
    return
  }
  currentValue.value = value
  hoverIndex.value = value
}

function resetCurrentValue() {
  if (props.readOnly || selected.value) {
    return
  }
  currentValue.value = numberRate.value
  hoverIndex.value = -1
}

function selectCurrentValue(value: number) {
  if (props.readOnly) {
    return
  }
  selected.value = true
  hoverIndex.value = value
  currentValue.value = value
  emits('update:rate', value)
  emits('selectRate', value)
}

watch(currentValue, (val) => {
  if (props.readOnly) {
    return
  }
  emits('update:rate', val)
})
</script>

<template>
  <div class="dj-rate" v-bind="$attrs">
    <span v-for="(item, key) in max" :key="key" @mousemove="setCurrentValue(item)" @mouseleave="resetCurrentValue" @click.stop="selectCurrentValue(item)">
      <DJIcon
        :color="item <= currentValue ? color.active : inactiveColor"
        :class="['dj-rate__icon', { hover: hoverIndex === item }, { hand: !readOnly }, { 'ml-2 mr-2': !readOnly }]"
        :size="size"
      >
        <IconStarFill :class="showDecimalIcon(item) && 'dj-rate__decimal--box'" />
        <template v-if="showDecimalIcon(item)">
          <DJIcon :style="decimalStyle" class="dj-rate__icon dj-rate__decimal" :size="size">
            <IconStarFill />
          </DJIcon>
        </template>
      </DJIcon>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.dj-rate {
  display: inline-block;
  align-items: center;
  &__icon {
    position: relative;
    display: inline-block;
    &.hover {
      transform: scale(1.15);
    }
  }
  &__decimal {
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    &--box {
      position: absolute;
      top: 0;
      left: 0;
    }
  }
}
</style>
